import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { ScrollArea } from '@/components/ui/ScrollArea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { cn } from '@/lib/utils';

import { useOpenInputsForCkJobId } from '../ck-debug.hooks';
import { ckDebugStore } from '../ck-debug.store';
import { getSnapshotOutcomeDisplay } from '../ck-debug.utils';

import type { CkSnapshot } from '../ck-debug.types';

function formatRelativeTime(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 1000) return 'now';
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const display = getSnapshotOutcomeDisplay(outcome);

  return (
    <span
      className={cn(
        'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase',
        display.bg,
        display.text
      )}
    >
      {display.label}
    </span>
  );
}

export const CkDebugEventTable = observer(() => {
  const snapshots = ckDebugStore.filteredSnapshots;
  const selectedId = ckDebugStore.selectedSnapshotId;
  const openInputsForCkJobId = useOpenInputsForCkJobId();

  const columns = useMemo<ColumnDef<CkSnapshot, unknown>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        cell: ({ row }) => (
          <span className="text-xs font-mono text-foreground-muted tabular-nums whitespace-nowrap">
            {formatRelativeTime(row.original.completedAt)}
          </span>
        ),
        size: 70,
      },
      {
        id: 'app',
        header: 'App',
        cell: ({ row }) => (
          <span className="text-xs text-foreground truncate block max-w-[120px]">
            {row.original.appName}
          </span>
        ),
        size: 120,
      },
      {
        id: 'trigger',
        header: 'Trigger',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted truncate block max-w-[100px]">
            {row.original.trigger}
          </span>
        ),
        size: 100,
      },
      {
        id: 'outcome',
        header: 'Outcome',
        cell: ({ row }) => <OutcomeBadge outcome={row.original.outcome} />,
        size: 70,
      },
      {
        id: 'windows',
        header: 'Windows',
        cell: ({ row }) => {
          const m = row.original.metrics;
          const parts: string[] = [];
          if (m.windowsProcessed > 0) parts.push(`${m.windowsProcessed}`);
          if (m.windowsFailed > 0) parts.push(`${m.windowsFailed}F`);
          if (m.windowsSkipped > 0) parts.push(`${m.windowsSkipped}S`);
          return (
            <span className="text-xs font-mono text-foreground-muted tabular-nums">
              {parts.join('/') || '0'}
            </span>
          );
        },
        size: 80,
      },
      {
        id: 'dedup',
        header: 'Dedup',
        cell: ({ row }) => {
          const m = row.original.metrics;
          const total =
            m.traversedItemsDeduplicated +
            m.parsedItemsDeduplicated +
            m.screenshotsDeduplicated;
          return (
            <span className="text-xs font-mono text-foreground-muted tabular-nums">
              {total}
            </span>
          );
        },
        size: 60,
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <span className="text-xs font-mono text-foreground-muted tabular-nums whitespace-nowrap">
            {row.original.metrics.totalDurationMs}ms
          </span>
        ),
        size: 80,
      },
      {
        id: 'inputs',
        header: 'Inputs',
        cell: ({ row }) => (
          <button
            className="text-xs text-primary hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              openInputsForCkJobId(row.original.jobId);
            }}
          >
            Open
          </button>
        ),
        size: 70,
      },
    ],
    [openInputsForCkJobId]
  );

  const table = useReactTable({
    data: snapshots,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.jobId,
  });

  return (
    <ScrollArea className="h-full">
      <Table>
        <TableHeader className="border-b border-border bg-surface sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-none">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className="h-7 px-2 text-xs text-foreground-subtle"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {snapshots.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-12">
                <span className="text-sm text-foreground-muted">
                  No snapshot events yet. Waiting for ContextKit data...
                </span>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isFailed = row.original.outcome === 'failed';
              const isTimeout =
                row.original.outcome === 'timeout' ||
                row.original.outcome === 'partial_timeout';
              const isSelected = row.original.jobId === selectedId;

              return (
                <TableRow
                  key={row.original.jobId}
                  onClick={() =>
                    ckDebugStore.selectSnapshot(
                      isSelected ? null : row.original.jobId
                    )
                  }
                  className={cn(
                    'h-7 cursor-pointer border-border hover:bg-surface-hover',
                    isSelected &&
                      'bg-secondary-active hover:bg-secondary-active',
                    isFailed && 'border-l-2 border-l-danger-9',
                    isTimeout && 'border-l-2 border-l-warning-9'
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-0 px-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
});
